import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, Building2, Mail, Phone, MapPin, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import centresService from '../../services/centresService';
import userService from '../../services/userService';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import './EditCentreDialog.css';

const EditCentreDialog = ({ isOpen, onClose, centre, onCentreUpdated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    is_active: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [devise, setDevise] = useState('');
  const [devisePreset, setDevisePreset] = useState('');
  const [deviseCustom, setDeviseCustom] = useState('');
  const [responsables, setResponsables] = useState([]);
  const [selectedResponsableIds, setSelectedResponsableIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const deviseOptions = ['EUR', 'USD', 'GBP', 'CHF', 'DZD', 'MAD', 'TND','AOA'];

  useEffect(() => {
    if (centre) {
      setFormData({
        name: centre.name || '',
        address: centre.address || '',
        phone: centre.phone || '',
        email: centre.email || '',
        is_active: centre.is_active ?? true,
      });
      setLogoFile(null);
      const existingDevise = centre.devise || '';
      if (deviseOptions.includes(existingDevise)) {
        setDevisePreset(existingDevise);
        setDeviseCustom('');
      } else if (existingDevise) {
        setDevisePreset('__OTHER__');
        setDeviseCustom(existingDevise);
      } else {
        setDevisePreset('');
        setDeviseCustom('');
      }
      setDevise(existingDevise);
      setError(null);
    }
  }, [centre]);

  useEffect(() => {
    const fetchResponsables = async () => {
      if (!isOpen || !centre) return;
      try {
        const response = await userService.getResponsables();
        const data = Array.isArray(response) ? response : response?.results || [];
        setResponsables(data);
        const preselected = data
          .filter((r) => (r.centres || []).some((cId) => String(cId) === String(centre.id)))
          .map((r) => String(r.id));
        setSelectedResponsableIds(preselected);
      } catch (err) {
        console.error('Error fetching responsables:', err);
        setResponsables([]);
        setSelectedResponsableIds([]);
      }
    };

    fetchResponsables();
  }, [isOpen, centre]);

  const responsableOptions = useMemo(() => {
    return (responsables || []).map((r) => ({
      value: String(r.id),
      label: `${r.first_name || ''} ${r.last_name || ''}`.trim() + (r.email ? ` (${r.email})` : ''),
    }));
  }, [responsables]);

  if (!isOpen || !centre) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError(t('centres.dialog.errors.nameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('address', formData.address || '');
      payload.append('phone', formData.phone || '');
      payload.append('email', formData.email || '');
      payload.append('is_active', String(!!formData.is_active));
      if (logoFile) payload.append('logo', logoFile);
      const finalDevise = devisePreset === '__OTHER__' ? deviseCustom : devisePreset;
      payload.append('devise', finalDevise || '');

      const response = await centresService.update(centre.id, payload);

      const selectedSet = new Set(selectedResponsableIds.map(String));
      const updates = (responsables || []).map(async (r) => {
        const currentCentreIds = (r.centres || []).map(String);
        const isSelected = selectedSet.has(String(r.id));

        const nextCentreIds = isSelected
          ? Array.from(new Set([...currentCentreIds, String(centre.id)]))
          : currentCentreIds.filter((id) => id !== String(centre.id));

        const nextSameAsCurrent =
          nextCentreIds.length === currentCentreIds.length &&
          nextCentreIds.every((id) => currentCentreIds.includes(id));

        if (nextSameAsCurrent) return;
        await userService.assignCentresToResponsable(r.id, nextCentreIds);
      });

      await Promise.all(updates);

      onCentreUpdated(response);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('centres.dialog.errors.update'));
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container ui-dialog-container">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <Building2 size={24} />
            <h2>{t('centres.dialog.editTitle')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {error && <div className="dialog-error">{error}</div>}

        <form className="dialog-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name" className="ui-label">
              <Building2 size={16} />
              {t('centres.dialog.form.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('centres.dialog.form.namePlaceholder')}
              required
              className="ui-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address" className="ui-label">
              <MapPin size={16} />
              {t('centres.dialog.form.address')}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('centres.dialog.form.addressPlaceholder')}
              rows={2}
              className="ui-textarea"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="ui-label">
                <Mail size={16} />
                {t('centres.dialog.form.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('centres.dialog.form.emailPlaceholder')}
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="ui-label">
                <Phone size={16} />
                {t('centres.dialog.form.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('centres.dialog.form.phonePlaceholder')}
                className="ui-input"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={!!formData.is_active}
                onChange={handleChange}
              />
              <span>{t('centres.dialog.form.active')}</span>
            </label>
          </div>

          <div className="form-group">
            <MultiSelectDropdown
              label={t('centres.dialog.form.responsablesOptional')}
              icon={UserPlus}
              options={responsableOptions}
              selectedValues={selectedResponsableIds}
              onChange={setSelectedResponsableIds}
              placeholder={t('centres.dialog.form.responsablesPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="logo" className="ui-label">
              Logo
            </label>
            {centre?.logo && (
              <a href={centre.logo} target="_blank" rel="noreferrer">
                {centre.logo}
              </a>
            )}
            <input
              type="file"
              id="logo"
              name="logo"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="ui-input"
            />
          </div>

          {/* Champ devise masqué - la devise du fournisseur est utilisée à la place
          <div className="form-group">
            <label htmlFor="devise" className="ui-label">
              Devise
            </label>
            <select
              id="devise"
              name="devise"
              value={devisePreset}
              onChange={(e) => {
                const v = e.target.value;
                setDevisePreset(v);
                setDevise(v);
                if (v !== '__OTHER__') setDeviseCustom('');
              }}
              className="ui-input"
            >
              <option value="">--</option>
              {deviseOptions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
              <option value="__OTHER__">Autre</option>
            </select>
            {devisePreset === '__OTHER__' && (
              <input
                type="text"
                value={deviseCustom}
                onChange={(e) => {
                  setDeviseCustom(e.target.value);
                  setDevise(e.target.value);
                }}
                className="ui-input"
              />
            )}
          </div>
          */}

          <div className="dialog-actions ui-dialog-actions">
            <button
              type="button"
              className="btn-secondary ui-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary ui-btn-primary"
              disabled={loading}
            >
              {loading ? t('common.saving') : (
                <>
                  <Save size={18} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCentreDialog;
